import { combineErrorStacks, logger } from '@/utils';
import { useCallback } from 'react';
import {
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
  useForm as _useForm,
} from 'react-hook-form';

export const useForm = <
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues | undefined = TFieldValues,
  TContext extends object = any,
>(
  params: UseFormProps<TFieldValues, TContext, TTransformedValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const { handleSubmit: _handleSubmit, ...hookForm } = _useForm<
    TFieldValues,
    TContext,
    TTransformedValues
  >(params);

  const handleSubmit: typeof _handleSubmit = useCallback(
    (onValid, onInvalid) => {
      return _handleSubmit(onValid, (fieldErrors) => {
        onInvalid?.(fieldErrors);
        const err = combineErrorStacks(
          new Error('failed to submit form'),
          new Error(),
        );
        const details: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(fieldErrors)) {
          details[key] = (value as { message?: string | undefined })?.message;
        }
        const detailsStr = JSON.stringify(details);
        logger.warn(
          `failed to submit form. ${detailsStr}`,
          {
            fieldErrors,
            details,
            details_str: detailsStr,
          },
          err,
        );
      });
    },
    [_handleSubmit],
  );

  return {
    ...hookForm,
    handleSubmit,
  };
};
